/** @file Weights panel view model: mesh, bones, selected vertices' influences, bone choices. */
import type { Doc, Id } from '@pose2d/format/types';
import type { MeshTarget } from '../../tools/editMesh/meshTarget';
import { staleBind } from '../../tools/editMesh/staleBind';
import { paintStroke } from '../../tools/paintWeights/paintStroke';
import { weightRows } from '../../tools/paintWeights/weightRows';

/** A bone with weight on the mesh (or the slot bone of a rigid mesh) and how many vertices it touches. */
export interface BoneRow {
  id: Id;
  name: string;
  vertices: number;
}

/** One bone's mean share over the selected vertices (0 where absent) and how many of them it touches. */
export interface InfluenceRow extends BoneRow {
  weight: number;
}

/** Everything the panel sections render. */
export interface WeightsView {
  target: MeshTarget;
  meshName: string;
  slotName: string;
  vertexCount: number;
  binding: 'rigid' | 'skinned';
  bones: BoneRow[];
  paintBone: Id;
  selected: number[];
  influences: InfluenceRow[];
  /** Every bone that can carry weight (socket bones excluded), by name. */
  choices: { id: Id; name: string }[];
  /** A weighted bone's bind pose differs from its setup world: weight edits would move vertices at setup (T-473). */
  stale: boolean;
}

const byName = (a: { name: string }, b: { name: string }): number => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0);

/** Sums of share and vertex count per bone over `vertices` of `rows`. */
function tally(doc: Doc, rows: readonly (readonly (readonly [Id, number])[])[], vertices: readonly number[]): InfluenceRow[] {
  const acc = new Map<Id, InfluenceRow>();
  for (const v of vertices) {
    for (const [bone, w] of rows[v] ?? []) {
      const row = acc.get(bone) ?? { id: bone, name: doc.bones[bone]?.name ?? bone, vertices: 0, weight: 0 };
      row.vertices += 1;
      row.weight += w / vertices.length;
      acc.set(bone, row);
    }
  }
  return [...acc.values()].sort(byName);
}

/** The panel's view of `target`; `selected` are vertex indices, `chosen` the brush's bone (null = slot bone). */
export function weightsView(doc: Doc, target: MeshTarget, selected: readonly number[], chosen: Id | null): WeightsView {
  const count = target.mesh.uvs.length / 2;
  const slotBone = paintStroke.bone(doc, target, null);
  const rows = weightRows.of(target.mesh) ?? Array.from({ length: count }, (): [Id, number][] => [[slotBone, 1]]);
  const all = Array.from({ length: count }, (_, v) => v);
  const choices = Object.entries(doc.bones).filter(([, b]) => b.kind === undefined).map(([id, b]) => ({ id, name: b.name })).sort(byName);
  return {
    target, meshName: target.mesh.name, slotName: doc.slots[target.slot]?.name ?? target.slot, vertexCount: count,
    binding: target.mesh.geometry.kind, bones: tally(doc, rows, all).map(({ id, name, vertices }) => ({ id, name, vertices })),
    paintBone: paintStroke.bone(doc, target, chosen), selected: [...selected], influences: tally(doc, rows, selected), choices,
    stale: staleBind.bones(doc, target.mesh).length > 0,
  };
}
